import { DataSource } from '@angular/cdk/table';
import { BehaviorSubject, Observable } from 'rxjs';
import { DocService } from '../../../services/doc.service';
import { finalize, tap } from 'rxjs/operators';
import { EventEmitter } from 'events';
import { DocViewerContextDoc, DocViewerContext } from '@models/ui/doc-viewer-context-doc.model';
import { EmailType } from '@models/email-type.model';

export class DocsDataSource implements DataSource<any> {
	/**
	 * The total number of documents in the latest result (not the size of a page - the whole set).
	 */
	private Empty = [];
	private docsSubject = new BehaviorSubject<any[]>(this.Empty);
	private loading$ = new BehaviorSubject<boolean>(false);

	public count = 0;
	public currentPageNumber = 0;
	public totalPages = 0;
	public initialized = false;
	public truncatedResult = false;

	public get loading() {
		return this.loading$.asObservable();
	}

	get curPage(): any[] {
		return this.docsSubject.value;
	}

	onError: EventEmitter = new EventEmitter();

	constructor(private docService: DocService) { }

	connect(): Observable<any[] | ReadonlyArray<any>> {
		return this.docsSubject.asObservable();
	}

	disconnect(): void {
		this.docsSubject.next(this.Empty);
		this.loading$.next(false);
	}

	clear() {
		this.docsSubject.next(this.Empty);
		this.count = 0;
		this.currentPageNumber = 0;
		this.totalPages = 0;
		this.initialized = false;
		this.onError.removeAllListeners();
	}

	loadDocsSubscription(
		pageIndex: number,
		pageSize: number,
		sortBy?: string,
		filterId?: string,
		includeCustomCodingFields?: boolean,
		includeFolderPath?: boolean,
		includeCustodianName?: boolean,
		includeReviewerNames?: boolean,
		expandEmailFamily?: boolean,
		isSearch?: boolean,
		searchQuery?: string,
		saveSearch?: boolean
	) {
		this.loading$.next(true);
		return this.docService.getDocs(pageIndex, pageSize, sortBy, filterId, includeCustomCodingFields, includeFolderPath, includeCustodianName, includeReviewerNames, expandEmailFamily, isSearch, searchQuery, saveSearch).pipe(
			tap(data => {
				if (!data) {
					this.onError.emit('doc-error');
				} else {
					this.count = data.pagination.totalItems;
					this.currentPageNumber = data.pagination.currentPage;
					this.totalPages = Math.trunc(this.count / pageSize);
					if (this.count % pageSize !== 0)
						this.totalPages++;
					this.docsSubject.next(data.docs);
					this.truncatedResult = data.pagination.truncatedResult;
					
					const contextMap: DocViewerContext = {
						includeEmailFamiles: expandEmailFamily,
						docList: []
					};
					data.docs?.map((x,idx) => {
						let displayType: 'native' | 'email' | 'attachment' = 'native';
						if (x.emailType.value === EmailType.EmailAttachment.value) {
							displayType = 'attachment';
						}
						else if (x.emailType.value === EmailType.EmailBody.value) {
							displayType = 'email';
						}
						const dest = new DocViewerContextDoc();
						dest.order = idx + 1;
						dest.docId = x.docId;
						dest.parentDocId = x.parentDocId;
						dest.docTitle = x.common.docTitle;
						dest.emailTypeId = x.emailTypeId;
						dest.displayType = displayType;
						dest.isExpandedResult = x.isExpandedResult;
						dest.childLevel = x.childLevel;
						contextMap.docList.push(dest);
					});
					localStorage.setItem('ContextMapping', JSON.stringify(contextMap));
				}
			}, error => {
				if (error === 'invalid query')
					this.onError.emit('query-error');
				else if (error === 'no-docs-error')
					this.onError.emit('no-docs-error');
				else
					this.onError.emit('doc-error');
			}),
			finalize(() => {
				this.loading$.next(false);
				this.initialized = true;
			}));
	}
}
